import {
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  MultiSelect,
} from '@otp/ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export enum CronTypes {
  'Day' = 'Day',
  'Week' = 'Week',
  'Month' = 'Month',
}

type CronProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  disabled?: boolean;
  className?: string;
  field: ControllerRenderProps<TFieldValues, TName>;
  type?: CronTypes;
};

const valueRangeMap = {
  [CronTypes.Day]: Array.from(Array(32).keys())
    .slice(1)
    .map((value) => {
      return {
        value: value,
        name: value.toString(),
      };
    }),
  [CronTypes.Week]: Array.from(Array(5).keys())
    .splice(1)
    .map((value) => {
      return {
        value: value,
        name: value.toString(),
      };
    }),
  [CronTypes.Month]: Array.from(Array(13).keys())
    .splice(1)
    .map((value) => {
      return {
        value: value,
        name: value.toString(),
      };
    }),
};

export const CronField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  disabled = false,
  className,
  field,
  type = CronTypes.Day,
}: CronProps<TFieldValues, TName>) => {
  const { t } = useTranslation();
  const typeOptions = useMemo(
    () =>
      Object.keys(CronTypes).map((key) => ({
        value: key,
        name: t(
          `most.list.schedule.form.fields.cron.key.${key.toLocaleLowerCase()}`,
        ),
      })),
    [t],
  );
  const [typeValue, setType] = useState(type);
  const [typeRange, setTypeRange] = useState(
    valueRangeMap[typeValue] as { name: string; value: number }[],
  );
  return (
    <>
      <MultiSelect
        key="ranges"
        multiple={false}
        fullWidth={true}
        className={className}
        options={typeRange}
        disabled={disabled}
        {...field}
        disableClearable={true}
        value={field.value.recurringValue as any}
        defaultValue={typeRange[0].value as any}
        onChange={(_event, value) => {
          field.onChange({
            ...field.value,
            recurringType: typeValue,
            recurringValue: value?.value,
          });
        }}
        ref={undefined}
      />
      <MultiSelect
        key="types"
        multiple={false}
        fullWidth={true}
        className={className}
        options={typeOptions}
        disabled={disabled}
        {...field}
        disableClearable={true}
        value={typeValue as any}
        ref={undefined}
        onChange={(_event, value) => {
          const newType = (value.value as CronTypes) || CronTypes.Day;
          const newRange = valueRangeMap[newType];
          const newValue = newRange[0].value;
          setType(newType);
          setTypeRange(newRange);
          field.onChange({
            ...field.value,
            recurringType: newType,
            recurringValue: newValue,
          });
        }}
      />
    </>
  );
};
